import React from "react";
import { Box, Typography, Button } from "@mui/material";
import { AccessTime } from "@mui/icons-material";
import { MessageBubbleContainer, MessageContent } from "./SharedStyledComponents";

const PaymentRequestMessage = ({
  message,
  isOwn,
  currentUser,
  storedUserId,
  formattedMessage,
  getFormattedMessageText,
  getCurrencySymbol,
  wrappedOnOpenPaymentDialog,
  getReadReceiptStatus,
  generatePaymentMessageText,
  theme,
}) => {
  const messageText = formattedMessage.text || "";
  const currentUserId = currentUser?.userId ?? storedUserId;

  const isUserSpecificRequest = messageText.includes("needs to pay");

  const orderId = message?.orderId;
  const currencySymbol = getCurrencySymbol(orderId?.currency || "USD");
  const paymentFlow = orderId?.paymentFlow;
  const paymentFlowStatus = paymentFlow?.status;
  const isApprovedPaymentRequest =
    (messageText.includes("approved") && messageText.includes("accepted")) ||
    paymentFlowStatus === "approved";

  // Check if current user is owner/team member for THIS specific order's business
  const orderBusinessId = orderId?.businessId?._id || orderId?.businessId;
  const orderLocationId = orderId?.locationId?._id || orderId?.locationId;
  let isCurrentUserOwnerOrTeamMemberForThisOrder = false;

  const employeeInfo = currentUser?.employee;
  if (employeeInfo) {
    const employeeBusinessId =
      employeeInfo?.businessId?._id || employeeInfo?.businessId;
    const isSameBusiness =
      employeeBusinessId &&
      orderBusinessId &&
      employeeBusinessId === orderBusinessId;

    let hasSameLocation = false;
    if (isSameBusiness) {
      const employeeLocationIds = Array.isArray(employeeInfo?.locationIds)
        ? employeeInfo.locationIds
        : [];
      hasSameLocation = employeeLocationIds.some((loc) => {
        const locId = loc?._id || loc;
        return orderLocationId && locId === orderLocationId;
      });
    }

    isCurrentUserOwnerOrTeamMemberForThisOrder = Boolean(
      isSameBusiness || hasSameLocation
    );
  }

  // Hide from business owners and team members of this order's business
  if (isCurrentUserOwnerOrTeamMemberForThisOrder) {
    return null;
  }

  if (isUserSpecificRequest) {
    const groupOrder = orderId?.groupOrder;
    const userOrders = groupOrder?.userOrders || [];

    const targetUserMatch = messageText.match(/^(\w+)\s+needs to pay/);
    const targetUserName = targetUserMatch ? targetUserMatch[1] : null;

    const initiatorUserOrder = userOrders.find(
      (userOrder) => userOrder.userFullName === targetUserName
    );

    if (!initiatorUserOrder) {
      return null;
    }

    const targetUserId =
      initiatorUserOrder.userId?._id || initiatorUserOrder.userId;

    if (targetUserId !== currentUserId) {
      return null;
    }

    return (
      <MessageBubbleContainer isOwn={isOwn}>
        {!isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: "#374151",
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              fontWeight: 600,
              mb: 0.5,
              alignSelf: "flex-start",
            }}
          >
            {message.senderId?.fullName ||
              message.senderId?.userName ||
              "user"}
          </Typography>
        )}

        <MessageContent
          isOwn={isOwn}
          isTemp={message.isTemp}
          isOrder={false}
          isPayment={true}
          isRequest={false}
          idDraft={false}
          isDisabled={false}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              minWidth: "280px",
              maxWidth: "380px",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: isOwn ? "#FFFFFF" : "#000000",
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: 1.3,
                textAlign: "left",
              }}
            >
              {getFormattedMessageText() || "Payment request"}
            </Typography>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: isOwn
                    ? "rgba(255, 255, 255, 0.8)"
                    : "rgba(0, 0, 0, 0.6)",
                  fontSize: "14px",
                }}
              >
                Your order total: {currencySymbol}{initiatorUserOrder?.subtotal || 0}
              </Typography>

              {(() => {
                const paymentSettings = orderId?.paymentSettings;
                const autoCollectUpfront =
                  paymentSettings?.autoCollectUpfront;
                const upfrontPercentage =
                  paymentSettings?.upfrontPercentage || 0;
                const orderTotal = initiatorUserOrder?.subtotal || 0;
                const upfrontAmount = autoCollectUpfront
                  ? (orderTotal * upfrontPercentage) / 100
                  : 0;

                if (autoCollectUpfront && upfrontAmount > 0) {
                  return (
                    <Typography
                      variant="body2"
                      sx={{
                        color: isOwn
                          ? "rgba(255, 255, 255, 0.8)"
                          : "rgba(0, 0, 0, 0.6)",
                        fontSize: "14px",
                      }}
                    >
                      Upfront amount ({upfrontPercentage}%): {currencySymbol}{upfrontAmount.toFixed(2)}
                    </Typography>
                  );
                }
                return null;
              })()}

              {initiatorUserOrder?.amountPaid > 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    color: isOwn
                      ? "rgba(255, 255, 255, 0.8)"
                      : "rgba(0, 0, 0, 0.6)",
                    fontSize: "14px",
                  }}
                >
                  Amount paid: {currencySymbol}{initiatorUserOrder.amountPaid}
                </Typography>
              )}

              <Typography
                variant="body2"
                sx={{
                  color: isOwn
                    ? "rgba(255, 255, 255, 0.8)"
                    : "rgba(0, 0, 0, 0.6)",
                  fontSize: "14px",
                }}
              >
                Remaining: {currencySymbol}{initiatorUserOrder?.remainingAmount ||
                  initiatorUserOrder?.amountToPay ||
                  0}
              </Typography>
            </Box>

            <Typography
              variant="h6"
              sx={{
                color: isOwn ? "#FFFFFF" : "#000000",
                fontSize: "16px",
                fontWeight: 700,
                lineHeight: 1,
              }}
            >
              total: {currencySymbol}{initiatorUserOrder?.amountToPay ||
                initiatorUserOrder?.subtotal ||
                0}
            </Typography>

            {initiatorUserOrder?.paymentStatus !== "upfront_paid" &&
              initiatorUserOrder?.paymentStatus !== "paid" &&
              (initiatorUserOrder?.amountToPay > 0 ||
                initiatorUserOrder?.remainingAmount > 0) && (
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    alignSelf: "flex-start",
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "#0085FF",
                      color: "white",
                      fontWeight: 700,
                      fontSize: "14px",
                      borderRadius: 2,
                      px: 2,
                      py: 1,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#0066CC",
                      },
                    }}
                    onClick={() => {
                      wrappedOnOpenPaymentDialog(message);
                    }}
                  >
                    pay
                  </Button>
                </Box>
              )}

            {(initiatorUserOrder?.paymentStatus === "upfront_paid" ||
              initiatorUserOrder?.paymentStatus === "paid") && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "rgba(34, 197, 94, 0.1)",
                  color: "#16a34a",
                  padding: theme.spacing(1, 2),
                  borderRadius: theme.spacing(1),
                  border: "1px solid rgba(34, 197, 94, 0.2)",
                  alignSelf: "flex-start",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  ✓ Payment completed
                </Typography>
              </Box>
            )}
            
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: isOwn ? "flex-end" : "flex-start",
                gap: 1,
                mt: 1,
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: isOwn ? "rgba(255, 255, 255, 0.7)" : "rgba(0,0,0,0.6)",
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                }}
              >
                {message.timestamp}
              </Typography>

              {isOwn && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  }}
                >
                  {message.isTemp ? (
                    <AccessTime fontSize="inherit" />
                  ) : (
                    getReadReceiptStatus() || "✓"
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </MessageContent>
      </MessageBubbleContainer>
    );
  }

  if (isApprovedPaymentRequest) {
    const initiatedBy = paymentFlow?.initiatedBy;
    const paymentType = paymentFlow?.type || orderId?.paymentType;
    const isSplitEvenly = paymentType === "split_evenly";

    if (isSplitEvenly) {
      const responses = orderId?.paymentFlow?.request?.responses || [];
      const isAcceptedUser = responses.some(
        (r) => r?.userId === currentUserId && r?.response === "approved"
      );

      if (!isAcceptedUser && initiatedBy !== currentUserId) {
        return null;
      }

      const groupOrder = orderId?.groupOrder;
      const userOrders = groupOrder?.userOrders || [];
      const currentUserOrder = userOrders.find((userOrder) => {
        const userId = userOrder.userId?._id || userOrder.userId;
        return userId === currentUserId;
      });
      const hasUserChosenOwnBill =
        currentUserOrder?.paymentType === "split_per_person";

      if (hasUserChosenOwnBill) {
        return null;
      }
    } else {
      if (paymentType === "pay_for_all") {
        const groupOrder = orderId?.groupOrder;
        const userOrders = groupOrder?.userOrders || [];
        const currentUserOrder = userOrders.find((userOrder) => {
          const userId = userOrder.userId?._id || userOrder.userId;
          return userId === currentUserId;
        });

        const isInitiator = initiatedBy === currentUserId;
        const hasNullPaymentType = currentUserOrder?.paymentType === null;

        if (!isInitiator && !hasNullPaymentType) {
          return null;
        }
      } else {
        if (initiatedBy && initiatedBy !== currentUserId) {
          return null;
        }

        if (!initiatedBy) {
          return null;
        }
      }
    }

    return (
      <MessageBubbleContainer isOwn={isOwn}>
        {!isOwn && (
          <Typography
            variant="caption"
            sx={{
              color: "#374151",
              fontSize: { xs: "0.75rem", sm: "0.8rem" },
              fontWeight: 600,
              mb: 0.5,
              alignSelf: "flex-start",
            }}
          >
            {message.senderId?.fullName ||
              message.senderId?.userName ||
              "user"}
          </Typography>
        )}

        <MessageContent
          isOwn={isOwn}
          isTemp={message.isTemp}
          isOrder={false}
          isPayment={true}
          isRequest={false}
          idDraft={false}
          isDisabled={false}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
              minWidth: "280px",
              maxWidth: "380px",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: isOwn ? "#FFFFFF" : "#000000",
                fontSize: "16px",
                fontWeight: 600,
                lineHeight: 1.3,
                textAlign: "left",
              }}
            >
              {generatePaymentMessageText()}
            </Typography>

            {(() => {
              const groupOrder = orderId?.groupOrder;
              const userOrders = groupOrder?.userOrders || [];
              const currentUserOrder = userOrders.find((userOrder) => {
                const userId = userOrder.userId?._id || userOrder.userId;
                return userId === currentUserId;
              });

              if (currentUserOrder?.paymentType === null) {
                return false;
              }

              return paymentFlow?.request?.responses;
            })() && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {(() => {
                  const responses = paymentFlow.request.responses;
                  const totalUsers = orderId?.contributors?.length || 0;
                  const approvedCount = responses.filter(
                    (r) => r.response === "approved"
                  ).length;
                  const rejectedCount = responses.filter(
                    (r) => r.response === "rejected"
                  ).length;
                  const noResponseCount = totalUsers - responses.length;

                  return (
                    <>
                      {approvedCount > 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: isOwn
                              ? "rgba(255, 255, 255, 0.8)"
                              : "rgba(0, 0, 0, 0.6)",
                            fontSize: "14px",
                          }}
                        >
                          {approvedCount}/{totalUsers} people voted 'thanks'
                        </Typography>
                      )}
                      {rejectedCount > 0 && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: isOwn
                              ? "rgba(255, 255, 255, 0.8)"
                              : "rgba(0, 0, 0, 0.6)",
                            fontSize: "14px",
                          }}
                        >
                          {rejectedCount}/{totalUsers} people voted 'thanks i
                          got mine'
                        </Typography>
                      )}
                    </>
                  );
                })()}
              </Box>
            )}

            {(() => {
              const groupOrder = orderId?.groupOrder;
              const userOrders = groupOrder?.userOrders || [];
              const currentUserOrder = userOrders.find((userOrder) => {
                const userId = userOrder.userId?._id || userOrder.userId;
                return userId === currentUserId;
              });

              if (currentUserOrder?.paymentType === null) {
                return false;
              }

              return true;
            })() && (
              <Typography
                variant="h6"
                sx={{
                  color: isOwn ? "#FFFFFF" : "#000000",
                  fontSize: "16px",
                  fontWeight: 700,
                  lineHeight: 1,
                }}
              >
                {(() => {
                  const currencyCode = orderId?.currency || "USD";
                  const currencySymbol = getCurrencySymbol(currencyCode);
                  const paymentSettings = orderId?.paymentSettings;
                  const autoCollectUpfront =
                    paymentSettings?.autoCollectUpfront;
                  const upfrontPercentage =
                    paymentSettings?.upfrontPercentage || 0;
                  const groupSubtotal =
                    orderId?.groupSubtotal || orderId?.totalAmount || 0;

                  const paymentType =
                    paymentFlow?.type || orderId?.paymentType;
                  const isSplitEvenly = paymentType === "split_evenly";

                  if (isSplitEvenly) {
                    const responses =
                      orderId?.paymentFlow?.request?.responses || [];
                    const acceptedUsers = responses.filter(
                      (r) => r?.response === "approved"
                    );
                    const totalUsers = acceptedUsers.length + 1;
                    const evenShare =
                      totalUsers > 0 ? groupSubtotal / totalUsers : 0;
                    const evenUpfrontAmount = autoCollectUpfront
                      ? (evenShare * upfrontPercentage) / 100
                      : 0;

                    if (autoCollectUpfront && evenUpfrontAmount > 0) {
                      return `upfront payment: ${currencySymbol}${evenUpfrontAmount.toFixed(
                        2
                      )}`;
                    } else {
                      return `total: ${currencySymbol}${evenShare.toFixed(2)}`;
                    }
                  } else {
                    const groupOrder = orderId?.groupOrder;
                    const userOrders = groupOrder?.userOrders || [];
                    const currentUserOrder = userOrders.find((userOrder) => {
                      const userId =
                        userOrder.userId?._id || userOrder.userId;
                      return userId === currentUserId;
                    });
                    const hasUserChosenSplitPerPerson =
                      currentUserOrder?.paymentType === "split_per_person";

                    if (hasUserChosenSplitPerPerson && currentUserOrder) {
                      const individualAmount =
                        currentUserOrder.amountToPay ||
                        currentUserOrder.subtotal ||
                        0;
                      const individualUpfrontAmount = autoCollectUpfront
                        ? (individualAmount * upfrontPercentage) / 100
                        : 0;

                      if (autoCollectUpfront && individualUpfrontAmount > 0) {
                        return `upfront payment: ${currencySymbol}${individualUpfrontAmount.toFixed(
                          2
                        )}`;
                      } else {
                        return `total: ${currencySymbol}${individualAmount}`;
                      }
                    } else {
                      let upfrontAmount = 0;

                      if (autoCollectUpfront) {
                        if (paymentType === "pay_for_all") {
                          const initiator = userOrders.find(
                            (user) => user.paymentType === "pay_for_all"
                          );

                          if (initiator) {
                            if (initiator.amountPaid > 0) {
                              upfrontAmount = initiator.amountPaid;
                            } else {
                              const unpaidUsers = userOrders.filter(
                                (user) =>
                                  user.amountPaid === 0 ||
                                  user.paymentStatus !== "paid"
                              );
                              const unpaidSubtotal = unpaidUsers.reduce(
                                (sum, user) => sum + (user.subtotal || 0),
                                0
                              );
                              upfrontAmount =
                                (unpaidSubtotal * upfrontPercentage) / 100;
                            }
                          }
                        } else {
                          upfrontAmount =
                            (groupSubtotal * upfrontPercentage) / 100;
                        }
                      }

                      if (autoCollectUpfront && upfrontAmount > 0) {
                        return `upfront payment: ${currencySymbol}${upfrontAmount.toFixed(
                          2
                        )}`;
                      } else {
                        return `total: ${currencySymbol}${groupSubtotal}`;
                      }
                    }
                  }
                })()}
              </Typography>
            )}

            {(() => {
              const paymentType = paymentFlow?.type || orderId?.paymentType;
              const isSplitEvenly = paymentType === "split_evenly";
              const isPayForAll = paymentType === "pay_for_all";

              const groupOrder = orderId?.groupOrder;
              const userOrders = groupOrder?.userOrders || [];
              const currentUserOrder = userOrders.find((userOrder) => {
                const userId = userOrder.userId?._id || userOrder.userId;
                return userId === currentUserId;
              });

              if (
                currentUserOrder?.paymentStatus === "upfront_paid" ||
                currentUserOrder?.paymentStatus === "paid"
              ) {
                return false;
              }

              if (isSplitEvenly) {
                return (
                  currentUserOrder?.paymentStatus !== "upfront_paid" &&
                  currentUserOrder?.paymentStatus !== "paid" &&
                  (currentUserOrder?.amountToPay > 0 ||
                    currentUserOrder?.remainingAmount > 0)
                );
              } else if (isPayForAll) {
                const isInitiator = initiatedBy === currentUserId;

                if (isInitiator) {
                  const initiatorUserOrder = userOrders.find((userOrder) => {
                    const userId = userOrder.userId?._id || userOrder.userId;
                    return userId === initiatedBy;
                  });
                  return (
                    initiatorUserOrder?.paymentStatus !== "upfront_paid" &&
                    initiatorUserOrder?.paymentStatus !== "paid"
                  );
                } else {
                  return false;
                }
              } else {
                const initiatorUserOrder = userOrders.find((userOrder) => {
                  const userId = userOrder.userId?._id || userOrder.userId;
                  return userId === initiatedBy;
                });
                return (
                  initiatorUserOrder?.paymentStatus !== "upfront_paid" &&
                  initiatorUserOrder?.paymentStatus !== "paid"
                );
              }
            })() && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  alignSelf: "flex-start",
                }}
              >
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#0085FF",
                    color: "white",
                    fontWeight: 700,
                    fontSize: "14px",
                    borderRadius: 2,
                    px: 2,
                    py: 1,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "#0066CC",
                    },
                  }}
                  onClick={() => {
                    wrappedOnOpenPaymentDialog(message);
                  }}
                >
                  pay
                </Button>
              </Box>
            )}

            {(() => {
              const paymentType = paymentFlow?.type || orderId?.paymentType;
              const isSplitEvenly = paymentType === "split_evenly";

              if (isSplitEvenly) {
                const groupOrder = orderId?.groupOrder;
                const userOrders = groupOrder?.userOrders || [];
                const currentUserOrder = userOrders.find((userOrder) => {
                  const userId = userOrder.userId?._id || userOrder.userId;
                  return userId === currentUserId;
                });
                return (
                  currentUserOrder?.paymentStatus === "upfront_paid" ||
                  currentUserOrder?.paymentStatus === "paid"
                );
              } else {
                const groupOrder = orderId?.groupOrder;
                const userOrders = groupOrder?.userOrders || [];
                const initiatorUserOrder = userOrders.find((userOrder) => {
                  const userId = userOrder.userId?._id || userOrder.userId;
                  return userId === initiatedBy;
                });
                return (
                  initiatorUserOrder?.paymentStatus === "upfront_paid" ||
                  initiatorUserOrder?.paymentStatus === "paid"
                );
              }
            })() && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "rgba(34, 197, 94, 0.1)",
                  color: "#16a34a",
                  padding: theme.spacing(1, 2),
                  borderRadius: theme.spacing(1),
                  border: "1px solid rgba(34, 197, 94, 0.2)",
                  alignSelf: "flex-start",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                  }}
                >
                  ✓ Upfront payment completed
                </Typography>
              </Box>
            )}
            
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: isOwn ? "flex-end" : "flex-start",
                gap: 1,
                mt: 1,
                width: "100%",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: isOwn ? "rgba(255, 255, 255, 0.7)" : "rgba(0,0,0,0.6)",
                  fontSize: { xs: "0.7rem", sm: "0.75rem" },
                }}
              >
                {message.timestamp}
              </Typography>

              {isOwn && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: { xs: "0.7rem", sm: "0.75rem" },
                  }}
                >
                  {message.isTemp ? (
                    <AccessTime fontSize="inherit" />
                  ) : (
                    getReadReceiptStatus() || "✓"
                  )}
                </Typography>
              )}
            </Box>
          </Box>
        </MessageContent>
      </MessageBubbleContainer>
    );
  }

  return null;
};

export default PaymentRequestMessage;
